/**
 * @file
 * React context carrying a run transcript's ambient values (run identity,
 * teammate display names, and the styleguide's static message source) so the
 * transcript's tool renderers can be static module-level components. Tool
 * renderers are registered in maps keyed by tool name; if those components were
 * built per render (closing over props), their identities would change whenever
 * an input changed and React would remount the whole rendered subtree — tearing
 * down nested live transcript streams — on every update while a tool call is
 * streaming. With the bound values in context, the component functions are
 * stable references and updates are plain re-renders.
 */
import type { AutomationId } from "@repo/app-types/entities/automation";
import type { AutomationArtifactId } from "@repo/app-types/entities/automationArtifact";
import type { AutomationRunId } from "@repo/app-types/entities/automationRun";
import type { UIMessage } from "ai";
import {
  createContext,
  useContext,
  useMemo,
  type FC,
  type ReactNode,
} from "react";

/**
 * Supplies a subagent thread's messages statically. The app leaves this unset —
 * nested threads stream live or fetch the persisted transcript — while the
 * styleguide injects fixture messages so the composed views render without any
 * data fetching.
 */
export type ResolveThreadMessages = (threadId: string) => readonly UIMessage[];

export interface RunTranscriptContextValue {
  playbookId: string | undefined;
  automationId: AutomationId;
  runId: AutomationRunId;
  /** Whether the run has finished — nested threads render static, not live. */
  isRunTerminal: boolean;
  /** Display names keyed by teammate id, for manager and recipient labels. */
  teammateNamesById: ReadonlyMap<string, string>;
  /**
   * Planned display names keyed by report key, read from the run's
   * `plan_reports` output, so delegation rows can name their subject exactly
   * as the plan did ("Mary Example's team", a group name).
   */
  reportNamesByKey: ReadonlyMap<string, string>;
  /** Styleguide override for the nested subagent threads (defaults to live/fetched). */
  resolveThreadMessages: ResolveThreadMessages | undefined;
  /**
   * Called once (per artifact id) when the run saves a report live — i.e. a
   * `save_report` tile streams in on a running run. The app wires this to
   * refresh the results list this run feeds; the styleguide leaves it unset (a
   * no-op). Not called while replaying a finished run's persisted transcript,
   * where the artifact already existed (see `runTranscript`).
   */
  onArtifactSaved?: (artifactId: AutomationArtifactId) => void;
}

const RunTranscriptContext = createContext<RunTranscriptContextValue | null>(
  null,
);

export type RunTranscriptProviderProps = Omit<
  RunTranscriptContextValue,
  "resolveThreadMessages" | "reportNamesByKey"
> & {
  resolveThreadMessages?: ResolveThreadMessages;
  reportNamesByKey?: ReadonlyMap<string, string>;
  children: ReactNode;
};

export const RunTranscriptProvider: FC<RunTranscriptProviderProps> = (
  props,
) => {
  const {
    playbookId,
    automationId,
    runId,
    isRunTerminal,
    teammateNamesById,
    reportNamesByKey = new Map(),
    resolveThreadMessages,
    onArtifactSaved,
    children,
  } = props;
  const value = useMemo<RunTranscriptContextValue>(
    () => ({
      playbookId,
      automationId,
      runId,
      isRunTerminal,
      teammateNamesById,
      reportNamesByKey,
      resolveThreadMessages,
      onArtifactSaved,
    }),
    [
      playbookId,
      automationId,
      runId,
      isRunTerminal,
      teammateNamesById,
      reportNamesByKey,
      resolveThreadMessages,
      onArtifactSaved,
    ],
  );
  return (
    <RunTranscriptContext.Provider value={value}>
      {children}
    </RunTranscriptContext.Provider>
  );
};

export function useRunTranscriptContext(): RunTranscriptContextValue {
  const value = useContext(RunTranscriptContext);
  if (!value) {
    throw new Error(
      "useRunTranscriptContext must be used inside a RunTranscriptProvider",
    );
  }
  return value;
}
